import { useAccount, useMsal } from "@azure/msal-react";
import * as Sentry from "@sentry/react";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { acquireAuthToken, checkApiHealth, getAPI } from "src/utils/fetch";

export interface SearchStatusResponse {
    data_sources: DatasourceIngestionStatus[];
}

export interface DatasourceIngestionStatus {
    id: string;
    total_count: number;
    ingestion_status: string;
    product_lines: ProductLineCount[];
    latest_document_timestamp: number;
    has_access: boolean;
}

export interface ProductLineCount {
    count: number;
    product_line: string;
}
const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
export const useIngestionStatus = (): UseQueryResult<SearchStatusResponse, Error> => {
    const { instance } = useMsal();
    const account = useAccount();
    const isAuthenticated = !!account?.idToken;

    return useQuery<SearchStatusResponse, Error>({
        queryKey: ["datasource_ingestion_status"],
        queryFn: async () => {
            const response = await getAPI({
                url: `${BACKEND_URL}/v1/search/status`,
                instance,
            });
            return await response.json();
        },
        enabled: isAuthenticated,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
    });
};

export const useApiHealth = () => {
    const { instance, inProgress } = useMsal();
    const account = useAccount();
    const isAuthenticated = !!account?.idToken;

    return useQuery({
        queryKey: ["api_health"],
        queryFn: async () => {
            try {
                const token = await acquireAuthToken(instance);
                const isHealthy = await checkApiHealth(token);

                return { isHealthy };
            } catch (error) {
                // Handle specific interaction_in_progress errors more gracefully
                if (error instanceof Error && error.message.includes("interaction_in_progress")) {
                    // Don't capture this as an error in Sentry, it's expected during auth flows
                    console.warn("API health check skipped due to ongoing authentication");
                    return { isHealthy: false };
                }

                // Handle auth interaction errors gracefully
                if (
                    error instanceof Error &&
                    error.message.includes("Authentication interaction in progress")
                ) {
                    console.warn("API health check skipped due to authentication interaction");
                    return { isHealthy: false };
                }

                // Capture the actual error in Sentry with additional context
                Sentry.withScope((scope) => {
                    scope.setTransactionName("api_health_check");
                    scope.setTag("error_type", "api_health_check");
                    scope.setExtra("username", account?.username);
                    scope.setExtra("account", account?.idTokenClaims?.gid);
                    scope.setExtra("org", account?.idTokenClaims?.org_code);
                    scope.setExtra("error_name", error instanceof Error ? error.name : "Unknown");
                    scope.setExtra(
                        "error_message",
                        error instanceof Error ? error.message : String(error),
                    );
                    scope.setExtra("backend_url", BACKEND_URL);
                    scope.setExtra("is_authenticated", isAuthenticated);
                    scope.setExtra("msal_in_progress", inProgress);

                    // Capture the actual error, not a generic message
                    Sentry.captureException(error, {
                        level: "error",
                        tags: {
                            component: "api_health_check",
                            error_source:
                                error instanceof Error && error.name === "AbortError"
                                    ? "timeout"
                                    : "unknown",
                        },
                    });
                });

                return { isHealthy: false };
            }
        },
        // Only run health check when user is authenticated and no MSAL interaction is in progress
        enabled: isAuthenticated && inProgress === "none",
        // Only refetch if the API was unhealthy
        refetchInterval: (query) => (query.state.data?.isHealthy ? false : 300000), // 5 minutes
        refetchIntervalInBackground: true,
        // Don't throw errors, we'll handle them in the UI
        throwOnError: false,
    });
};
